import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {useAgent, useSession} from '#/state/session'
import {usePrivateStore} from './privateStore'
import {getPrivacySettings, getPrivateState} from './PrivateAccountService'

export function usePrivatePrivacySettings(did?: string) {
  const agent = useAgent()
  return useQuery({
    queryKey: ['private-privacy', did],
    queryFn: async () => {
      if (!did) return null
      return getPrivacySettings(did, agent)
    },
    enabled: !!did,
    staleTime: 5 * 60 * 1000,
  })
}

export function usePrivateProfileState({
  did,
  checkMembership = false,
}: {
  did?: string
  checkMembership?: boolean
}) {
  const agent = useAgent()
  const {currentAccount} = useSession()
  const cached = usePrivateStore(state =>
    did ? state.profiles[did] : undefined,
  )

  const query = useQuery({
    queryKey: ['private-state', did, checkMembership],
    queryFn: async () => {
      if (!did) return null
      return getPrivateState({
        targetDid: did,
        agent,
        checkMembership,
      })
    },
    enabled: !!did,
    staleTime: 60 * 1000,
  })

  const isOwner = useMemo(
    () => !!did && currentAccount?.did === did,
    [did, currentAccount?.did],
  )

  return {
    state: query.data ?? cached ?? null,
    isLoading: query.isLoading,
    isOwner,
  }
}
